import { useQueryClient } from "@tanstack/react-query";
import { LogOut, Search } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@krea/shared/design-system/Button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@krea/shared/design-system/input/input-group";
import { KreaLogo } from "@krea/shared/design-system/logo/krea";
import { Separator } from "@krea/shared/design-system/Separator";
import { Spinner } from "@krea/shared/design-system/Spinner";
import { useAuthStore } from "@krea/shared/features/auth/authStore";
import { useDebounce } from "@krea/shared/utils/use-debounce";

import { Footer } from "@/components/footer";
import { MeAvatar } from "@/features/users/components/me-avatar";
import { useGetMeQuery } from "@/features/users/hooks/use-get-me.query";

import { CompanyCard } from "../components/lender-company-card";
import { useCurrentLenderCompanyStore } from "../current-lender-company-store";
import { useGetMyLenderCompaniesQuery } from "../hooks/use-get-my-lender-companies.query";

export const SelectLenderCompanyPage = () => {
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 500);
  const { data: me } = useGetMeQuery();
  const companiesQuery = useGetMyLenderCompaniesQuery(
    {
      limit: 100,
      offset: 0,
      ...(me?.isAdmin && {
        search: debouncedSearch,
      }),
    },
    {
      enabled: !!me,
    },
  );

  const resetStore = useAuthStore((state) => state.reset);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const setCurrentLenderCompany = useCurrentLenderCompanyStore(
    (state) => state.setCurrentLenderCompany,
  );
  const companies =
    companiesQuery.data?.pages.flatMap((page) => page.items) ?? [];

  const handleLogout = () => {
    resetStore();
    queryClient.clear();
    navigate("/login");
  };

  const onSelectCompany = (companyId: number) => {
    setCurrentLenderCompany(companyId);
    queryClient.resetQueries();
    navigate(`/lender-company/${companyId}`);
  };

  return (
    <div className="flex min-h-screen flex-col bg-white text-black">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-8 sm:py-6">
          <KreaLogo />

          {/* Mobile stacks nicely */}
          <div className="flex items-center gap-3 sm:gap-6">
            <MeAvatar />

            <Button variant="secondary" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex h-full flex-1 items-center justify-center bg-gray-100 px-4 py-10 sm:px-8 sm:py-16">
        <div className="w-full max-w-5xl">
          <div className="mb-10 text-center sm:mb-12">
            <h1 className="mb-3 text-page-title">Select a Lender Company</h1>
            <p className="text-base text-gray-600 sm:text-lg">
              Choose which company you&apos;d like to access
            </p>
          </div>

          {me?.isAdmin && (
            <div className="mx-auto mb-8 w-full max-w-2xl bg-white">
              <InputGroup>
                <InputGroupInput
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search companies..."
                  className="w-full"
                  aria-label="Search companies"
                />
                <InputGroupAddon align="inline-end">
                  <InputGroupText>
                    <Search className="h-5 w-5" />
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            </div>
          )}
          {companiesQuery.isLoading && <Spinner />}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {companies.map((c) => (
              <CompanyCard key={c.id} company={c} onSelect={onSelectCompany} />
            ))}
          </div>
          {companiesQuery.isError && (
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <p className="font-semibold">Couldn’t load companies</p>
              <p className="mt-1 text-helper">Please try again.</p>
              <div className="mt-4">
                <Button onClick={() => companiesQuery.refetch()}>Retry</Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Separator />

      {/* Footer */}
      <Footer />
    </div>
  );
};
